import {DocumentPriceBasis} from "@/components/docs/DocumentPriceBasis";
/**
 * Purchase-order editor — its OWN route, not an overlay: 19 header controls plus
 * a line grid is minutes of work, and an overlay has no URL to come back to.
 *
 * Money discipline (CLAUDE.md): unit_price / total_price / subtotal / tax /
 * total / shipping are INTEGER at 1/10,000. `taxPercentage` is NOT money — it
 * is percent × 100 (12.5% → 1250) and goes through parsePercent, never
 * parseMoneyMajor. Nothing here multiplies or divides by 100 to convert money.
 *
 * The totals strip mirrors apps/api/src/services/purchaseOrders.ts buildItems()
 * exactly (subtotal → tax → total) so the preview cannot drift from what the
 * server stores.
 */
import { useEffect, useRef, useState } from "react";
import { Navigate, useBlocker, useLocation, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Printer, Trash2 } from "lucide-react";
import { PoPrintPreview } from "@/components/purchaseOrders/PoPrintPreview";
import { PurchaseOrderFromPiPage } from "./PurchaseOrderFromPiPage";
import type { PiSummaryDto, PoStatus, SupplierDto } from "@hisense/shared";
import { poTotalsMinor, vnDate } from "@hisense/shared";
import { PageActions, PageBody, PageHeader } from "@/components/ui/PageHeader";
import { PrintLanguageToggle } from "@/components/docs/PrintLanguageToggle";
import { usePageCrumbs } from "@/components/shell/breadcrumb";
import { Button } from "@/components/ui/Button";
import { Input, FieldLabel } from "@/components/ui/Input";
import { Select } from "@/components/ui/Select";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { ErrorState, LoadingState } from "@/components/ui/States";
import {
  PoLinesEditor,
  poDraftsToInput,
  poLineFromDto,
  poLinesSubtotal,
  type PoLineDraft,
} from "@/components/purchaseOrders/PoLinesEditor";
import { PoPiLinkPanel, poWriteError } from "@/components/purchaseOrders/PoPiLinkPanel";
import { useAuth } from "@/app/auth";
import { useCan } from "@/app/access";
import {
  useCreatePurchaseOrder,
  useDeletePurchaseOrder,
  usePurchaseOrder,
  useSettings,
  useSupplier,
  useSuppliers,
  useUpdatePurchaseOrder,
} from "@/api/hooks";
import { formatMoney, unitPriceToInput } from "@/domain/money";
import { translateStatus } from "@/domain/i18n";
import { ApiClientError, getLocalizedError } from "@/lib/api";
import { parseMoneyMajor, parsePercent, percentToInput } from "@/lib/moneyInput";
import { SaveButton } from "@/components/ui/SaveButton";
import { FieldGrid, FormGroup, Hint, MoreBlock, SPAN_CLASS } from "@/components/ui/FormLayout";
import { emptyPoHeader, poSideColumnsUsed, type PoHeaderDraft } from "@/components/purchaseOrders/poFormLayout";

const toDateInput = (ms: number | null | undefined) =>
  ms ? vnDate(ms) : "";
const fromDateInput = (s: string) => (s ? new Date(s + "T00:00:00Z").getTime() : undefined);

const PO_STATUSES: readonly PoStatus[] = [
  "Draft",
  "Sent",
  "Confirmed",
  "Partially Delivered",
  "Delivered",
  "Cancelled",
];

/**
 * Remount on id change so a cached record's form state can never bleed into the
 * next one (CLAUDE.md remount rule). The route element is stable across
 * /purchase-orders/:id → /purchase-orders/:otherId, so the key has to live here.
 */
export function PurchaseOrderEditorPage() {
  const { id } = useParams();
  const {search} = useLocation();
  if ((!id || id === "new") && new URLSearchParams(search).get("mode") === "pi") return <PurchaseOrderFromPiPage/>;
  return <PurchaseOrderEditorForm key={id ?? "new"} />;
}

function PurchaseOrderEditorForm() {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const {search} = useLocation();
  const params = new URLSearchParams(search); params.delete("mode");
  const context = params.size ? `?${params}` : "";
  const listUrl = `/purchase-orders${context}`;
  const { user } = useAuth();
  const can = useCan();
  const isNew = !id || id === "new";

  const { data: existing, isLoading, error: loadError, refetch } = usePurchaseOrder(isNew ? undefined : id);
  const suppliers = useSuppliers({ q: "" });
  const settings = useSettings();
  const create = useCreatePurchaseOrder();
  const update = useUpdatePurchaseOrder(id ?? "new");
  const del = useDeletePurchaseOrder();

  const lists = settings.data?.businessOperations.optionLists;
  const currencies = lists?.currencies ?? [];
  const priorities = lists?.poPriorities ?? [];
  const paymentTermsOptions = lists?.paymentTerms ?? [];
  const shippingMethodOptions = lists?.shippingMethods ?? [];

  // Đơn của nhà cung cấp riêng tư mình không được thấy (28/10/2026): chỉ xem — máy chủ cũng chặn sửa (`private_supplier`).
  const supplierHidden = existing?.supplierHidden === true;
  const canEdit = user ? can(isNew ? "records:create" : "records:update") && !supplierHidden : false;
  const canDelete = user ? can("records:delete") && !supplierHidden : false;

  const [h, setH] = useState<PoHeaderDraft>(emptyPoHeader);
  /** "Hiện cột phụ" — có dòng dùng cột phụ thì cột phụ luôn hiện (poSideColumnsUsed). */
  const [sideOpen, setSideOpen] = useState(false);
  const [lines, setLines] = useState<PoLineDraft[]>([]);
  const [currencyPricesReviewed, setCurrencyPricesReviewed] = useState(false);
  const [linkedPi, setLinkedPi] = useState<{ id: string; piNo: string | null } | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Mốc lần lưu thành công gần nhất — nút Lưu đổi thành "Đã lưu" trong 2
  // giây. Không có tín hiệu nào thì người dùng bấm thêm lần nữa cho chắc.
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const [dirty, setDirty] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const initialized = useRef(false);
  const allowNavigation = useRef(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const saving = create.isPending || update.isPending;
  const busy = saving || del.isPending;
  const blocker = useBlocker(({currentLocation,nextLocation}) => !allowNavigation.current && (dirty || busy) && (currentLocation.pathname !== nextLocation.pathname || currentLocation.search !== nextLocation.search));
  const [pendingSupplierId, setPendingSupplierId] = useState<string | null>(null);

  useEffect(() => {
    if (!existing || initialized.current) return;
    initialized.current = true;
    setH({
      status: existing.status,
      currency: existing.currency,
      supplierId: existing.supplierId ?? "",
      supplierContact: existing.supplierContact ?? "",
      orderDate: toDateInput(existing.orderDate),
      expectedDeliveryDate: toDateInput(existing.expectedDeliveryDate),
      actualDeliveryDate: toDateInput(existing.actualDeliveryDate),
      priority: existing.priority ?? "",
      paymentTerms: existing.paymentTerms ?? "",
      shippingMethod: existing.shippingMethod ?? "",
      shipping: existing.shipping ? unitPriceToInput(existing.shipping) : "",
      taxPercentage: percentToInput(existing.taxPercentage),
      remarks: existing.remarks ?? "",
      printLanguage: existing.printLanguage ?? "en",
    });
    setLines(existing.items.map(poLineFromDto));
    setLinkedPi(existing.linkedPiId ? { id: existing.linkedPiId, piNo: existing.linkedPiNo } : null);
    setDirty(false);
  }, [existing]);

  // A page survives the mis-click that killed the old overlay, but not a reload
  // or a closed tab — the only unsaved-work loss left, so guard exactly that.
  useEffect(() => {
    if (!dirty && !busy) return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => { if (!allowNavigation.current) {e.preventDefault();e.returnValue="";} };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [dirty, busy]);

  const supplierRows = suppliers.data?.data ?? [];
  const supplierId = h.supplierId || null;
  // The whole PO module sits behind supplier_cost:view, so the supplier record
  // is readable here; the detail DTO's joined columns are the fallback while the
  // list/detail queries are still in flight.
  const supplierDetail = useSupplier(supplierId ?? undefined);
  const listedSupplier: SupplierDto | undefined = supplierRows.find((s) => s.id === supplierId);
  const useSnapshot = !!existing && existing.supplierId === supplierId;
  const supplierName = useSnapshot ? existing.supplierName : supplierDetail.data?.companyName ?? listedSupplier?.companyName ?? null;
  const supplierAddress = useSnapshot ? existing.supplierAddress : supplierDetail.data?.address ?? listedSupplier?.address ?? null;
  const supplierPhone = useSnapshot ? existing.supplierPhone : supplierDetail.data?.phone ?? listedSupplier?.phone ?? null;
  const supplierEmail = useSnapshot ? existing.supplierEmail : supplierDetail.data?.email ?? listedSupplier?.email ?? null;
  /**
   * Người liên hệ bên nhà cung cấp — CHỌN được, và được CHỐT lên đơn.
   *
   * Cột `purchase_orders.supplier_contact` vốn đã có và service vẫn ghi, nhưng
   * form chỉ hiện người liên hệ ĐẦU TIÊN ở dạng chỉ đọc — nên một nhà cung cấp
   * có ba người phụ trách ba nhóm hàng thì đơn nào cũng gửi cho đúng một người.
   * Hệ cũ cho chọn.
   */
  const supplierContacts = supplierDetail.data?.contacts ?? [];

  const patch = (p: Partial<typeof h>) => {
    if (p.currency !== undefined && p.currency.trim().toUpperCase() !== h.currency.trim().toUpperCase()) {
      setLines(previous => previous.map(line => ({ ...line, unitPrice: "" })));
      setCurrencyPricesReviewed(true);
    }
    setDirty(true);
    setH((prev) => ({ ...prev, ...p }));
  };
  const patchLines = (next: PoLineDraft[]) => {
    setDirty(true);
    setLines(next);
  };

  /** Legacy handleSupplierSelect: a new supplier invalidates every line. */
  const applySupplierChange = (nextId: string) => {
    setDirty(true);
    setLines([]);
    setH((prev) => ({ ...prev, supplierId: nextId, supplierContact: "" }));
  };

  const onSupplierChange = (nextId: string) => {
    if (nextId === h.supplierId) return;
    if (lines.length) {
      setPendingSupplierId(nextId);
      return;
    }
    applySupplierChange(nextId);
  };

  const taxParsed = parsePercent(h.taxPercentage);
  const shippingParsed = parseMoneyMajor(h.shipping);
  // Mirrors poService.buildItems() step for step: subtotal is already rounded by
  // poLinesSubtotal (totalMinor), tax = subtotal × pct/10000, total adds shipping.
  // An unparseable field contributes 0 to the PREVIEW only — save refuses it.
  const totals = (() => {
    const subtotal = poLinesSubtotal(lines);
    const taxPct = taxParsed.invalid ? 0 : (taxParsed.value ?? 0);
    const shipping = shippingParsed.invalid ? 0 : (shippingParsed.minor ?? 0);
    return poTotalsMinor(subtotal, taxPct, shipping);
  })();

  usePageCrumbs(
    isNew
      ? [{ label: t("purchaseOrders.new") }]
      : existing
        ? [{ label: existing.poNumber }]
        : [],
    listUrl,
  );

  if (!isNew && isLoading) {
    return (
      <PageBody>
        <LoadingState label={t("purchaseOrders.loading")} />
      </PageBody>
    );
  }

  if(!isNew&&loadError instanceof ApiClientError&&loadError.code==="sourcing_preview_required")return <Navigate replace to={`/sourcing/orders/${id}`}/>;
  if (!isNew && loadError && !existing) {
    const status = loadError instanceof ApiClientError ? loadError.status : 0;
    return (
      <PageBody>
        <ErrorState
          message={
            status === 403
              ? t("errors.forbidden")
              : status === 404
                ? t("purchaseOrders.notFound")
                : t("purchaseOrders.errorLoad")
          }
          onRetry={status === 403 || status === 404 ? undefined : () => void refetch()}
        />
      </PageBody>
    );
  }

  if (!isNew && !existing) {
    return (
      <PageBody>
        <ErrorState message={t("purchaseOrders.notFound")} />
      </PageBody>
    );
  }

  const save = async () => {
    if (busy || !canEdit) return;
    setError(null);

    if (!h.supplierId) return setError(t("purchaseOrders.supplierRequired"));
    if (!h.orderDate) return setError(t("poWorkspace.orderDateRequired"));
    if (taxParsed.invalid) return setError(t("purchaseOrders.invalidTax"));
    if (shippingParsed.invalid) {
      return setError(t("common.invalidField", { field: t("poWorkspace.shippingFee") }));
    }
    const conv = poDraftsToInput(lines);
    if ("error" in conv) {
      return setError(
        t("common.lineError", { line: conv.error.line, field: t(`common.${conv.error.field}`) }),
      );
    }
    if (conv.items.length === 0) return setError(t("purchaseOrders.itemsRequired"));

    const payload = {
      supplierId: h.supplierId,
      supplierContact: h.supplierContact.trim() || null,
      orderDate: fromDateInput(h.orderDate),
      expectedDeliveryDate: fromDateInput(h.expectedDeliveryDate) ?? null,
      actualDeliveryDate: fromDateInput(h.actualDeliveryDate) ?? null,
      paymentTerms: h.paymentTerms || null,
      shippingMethod: h.shippingMethod || null,
      priority: h.priority || null,
      status: h.status,
      currency: h.currency || "USD",
      pricesReviewed: currencyPricesReviewed,
      shipping: shippingParsed.minor ?? 0,
      taxPercentage: taxParsed.value ?? 0,
      linkedPiId: linkedPi?.id ?? null,
      remarks: h.remarks.trim() || null,
      printLanguage: h.printLanguage,
      // ALWAYS the complete array: poRepo.update batches delete+insert, so an
      // omitted `items` would leave stale rows carrying the old supplier tag.
      items: conv.items,
    };

    try {
      if (isNew) {
        const r = await create.mutateAsync(payload);
        setDirty(false);
        allowNavigation.current = true;
        navigate(`/purchase-orders/${r.data.id}${context}`, { replace: true });
      } else {
        await update.mutateAsync(payload);
        setDirty(false);
        setSavedAt(Date.now());
        setCurrencyPricesReviewed(false);
      }
    } catch (err) {
      // getLocalizedError ignores error.fields, so the interpolated messages are
      // built here, where the fields are available. supplier_mismatch names the
      // supplier the line SHOULD have matched — only this form knows which that is.
      if (err instanceof ApiClientError && err.code === "supplier_mismatch") {
        setError(
          t("purchaseOrders.supplierMismatch", {
            product: err.fields?.product ?? "—",
            supplier: supplierName ?? "—",
          }),
        );
      } else {
        // Shared with the PI panel: interpolates pi_already_linked {number, po}.
        setError(poWriteError(err, t, "purchaseOrders.errorSave"));
      }
    }
  };

  const onDelete = async () => {
    if (!existing) return;
    try {
      await del.mutateAsync(existing.id);
      setDirty(false);
      allowNavigation.current = true;
      navigate(listUrl);
    } catch (err) {
      setError(getLocalizedError(err, t, "purchaseOrders.errorDelete"));
    }
  };

  const readOnly = !canEdit || busy;
  const sideUsed = poSideColumnsUsed(lines);
  const showSide = sideOpen || sideUsed;
  const numberCls = "font-mono tabular [&_input]:text-right";

  /*
   * Bố cục (19/09/2026, cùng chuẩn PI, báo giá và đơn hàng mẫu — poFormLayout.ts): tiêu đề + việc của chứng từ ở đầu trang (không còn
   * nút Lưu ở đó); Nhà cung cấp; Chi tiết đơn hàng (liên kết PI ở cuối nhóm); Hàng hoá (bảng độ rộng cố định + khối tổng tiền);
   * Ghi chú; một thanh Hủy / Lưu dính đáy.
   */
  return (
    <PageBody className="space-y-4">
      {previewOpen && existing && <PoPrintPreview po={existing} onClose={()=>setPreviewOpen(false)}/>}
      <ConfirmDialog open={blocker.state === "blocked"} title={t("common.unsavedTitle")} description={t("common.unsavedDescription")}
        confirmLabel={t("common.discardChanges")} cancelLabel={t("common.keepEditing")} busy={busy}
        onCancel={()=>blocker.reset?.()} onConfirm={()=>blocker.proceed?.()}/>
      <ConfirmDialog
        open={confirmDelete}
        title={existing?.poNumber ?? t("common.delete")}
        description={t("purchaseOrders.confirmDelete", { number: existing?.poNumber ?? "" })}
        confirmLabel={t("common.delete")}
        cancelLabel={t("common.cancel")}
        busy={del.isPending}
        onCancel={() => setConfirmDelete(false)}
        onConfirm={() => void onDelete()}
      />
      <ConfirmDialog
        open={pendingSupplierId !== null}
        title={t("common.supplier")}
        description={t("purchaseOrders.supplierChangeClears")}
        confirmLabel={t("common.continue")}
        cancelLabel={t("common.cancel")}
        tone="warn"
        onCancel={() => setPendingSupplierId(null)}
        onConfirm={() => {
          if (pendingSupplierId === null) return;
          applySupplierChange(pendingSupplierId);
          setPendingSupplierId(null);
        }}
      />
      <PageHeader
        // Đơn mua không có trang xem riêng: người chỉ có quyền xem cũng mở trang này, nên tiêu đề không nói "Sửa" với họ.
        title={isNew ? t("purchaseOrders.new") : canEdit ? t("poWorkspace.editTitle") : t("purchaseOrders.document")}
        subtitle={isNew ? t("purchaseOrders.numberYearHint") : [existing?.poNumber, supplierName].filter(Boolean).join(" · ")}
        actions={
          <PageActions>
            {/* Ngôn ngữ in là dữ liệu của đơn: đổi là thay đổi chưa lưu — bản in lấy ngôn ngữ ĐÃ LƯU (printPo). */}
            {/* Người chỉ xem không đổi được: đổi là thành thay đổi chưa lưu mà họ không lưu được. */}
            <PrintLanguageToggle value={h.printLanguage} onChange={(lang) => patch({ printLanguage: lang })} disabled={readOnly} />
            {isNew && <Button variant="secondary" size="sm" disabled={busy} onClick={()=>navigate(`/purchase-orders/new${context ? context + "&" : "?"}mode=pi`)}>{t("poWorkspace.fromPi")}</Button>}
            {/* In được cho MỌI vai trò đọc được đơn: đây là tờ giấy gửi cho nhà
                cung cấp, không phải thao tác sửa dữ liệu. Chỉ ẩn khi đơn chưa lưu —
                chưa có số PO thì chưa có gì để gửi. */}
            {!isNew && existing && (
              <Button variant="secondary" size="sm" disabled={dirty || busy} title={dirty ? t("poWorkspace.saveBeforePrint") : undefined} onClick={() => setPreviewOpen(true)}>
                <Printer className="size-4" /> {t("poWorkspace.previewPrint")}
              </Button>
            )}
            {!isNew && canDelete && (
              <Button variant="danger" size="sm" onClick={() => setConfirmDelete(true)} disabled={del.isPending} aria-label={t("common.delete")}>
                <Trash2 className="size-4" />
              </Button>
            )}
          </PageActions>
        }
      />
      {!canEdit && (
        <p className="rounded-sm bg-sunken px-3 py-2 text-xs text-ink-soft">{supplierHidden ? t("common.internalSupplierHint") : t("errors.readOnly")}</p>
      )}

      {/* ── Nhà cung cấp — đổi nhà cung cấp xoá dòng hàng (hỏi trước). ── */}
      <FormGroup id="po-group-supplier" title={t("purchaseOrders.supplierSection")} hint={t("poWorkspace.groups.supplierHint")}>
        <FieldGrid>
          <div className={SPAN_CLASS[6]}>
            <FieldLabel htmlFor="po-supplier">{t("common.supplier")}</FieldLabel>
            <Select
              id="po-supplier"
              containerClassName="w-full"
              disabled={readOnly}
              value={h.supplierId}
              onChange={(e) => onSupplierChange(e.target.value)}
            >
              <option value="">—</option>
              {supplierRows.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.companyName}
                </option>
              ))}
              {/* A supplier that has since been archived must still show. */}
              {h.supplierId && !supplierRows.some((s) => s.id === h.supplierId) && supplierName && (
                <option value={h.supplierId}>{supplierName}</option>
              )}
            </Select>
            <Hint>{t("purchaseOrders.supplierChangeClears")}</Hint>
          </div>
          <div className={SPAN_CLASS[6]}>
            <FieldLabel htmlFor="po-contact">{t("purchaseOrders.contactPerson")}</FieldLabel>
            <Select
              id="po-contact"
              containerClassName="w-full"
              disabled={!canEdit}
              value={h.supplierContact ?? ""}
              onChange={(e) => patch({ supplierContact: e.target.value })}
            >
              <option value="">{t("purchaseOrders.selectContact")}</option>
              {supplierContacts.map((c) => (
                <option key={c.id} value={c.name}>
                  {[c.name, c.phone1].filter(Boolean).join(" · ")}
                </option>
              ))}
              {/* Người liên hệ đã chốt nhưng không còn trong danh bạ vẫn phải
                  hiện, nếu không mở một PO cũ ra là ô tự rỗng. */}
              {h.supplierContact && !supplierContacts.some((c) => c.name === h.supplierContact) && (
                <option value={h.supplierContact}>{h.supplierContact}</option>
              )}
            </Select>
          </div>
        </FieldGrid>
        {h.supplierId && (
          <div className="rounded-sm border border-line bg-sunken/40 px-3 py-2.5">
            <p className="text-xs font-medium text-ink-soft">{t(useSnapshot ? "poWorkspace.supplierSnapshot" : "purchaseOrders.supplierReadOnly")}</p>
            <dl className="mt-1 grid grid-cols-[6rem_minmax(0,1fr)] gap-x-3 gap-y-1 text-xs">
              <dt className="text-ink-faint">{t("poWorkspace.supplierPhone")}</dt><dd className="text-ink">{supplierPhone || "—"}</dd>
              <dt className="text-ink-faint">{t("poWorkspace.supplierEmail")}</dt><dd className="text-ink">{supplierEmail || "—"}</dd>
              <dt className="text-ink-faint">{t("poWorkspace.supplierAddress")}</dt><dd className="whitespace-pre-line text-ink">{supplierAddress || "—"}</dd>
            </dl>
          </div>
        )}
      </FormGroup>

      {/* ── Chi tiết đơn hàng — hàng 1 tiến độ, hàng 2 điều khoản mua. ── */}
      <FormGroup id="po-group-order" title={t("purchaseOrders.orderDetails")} hint={t("poWorkspace.groups.orderHint")}>
        <div className="grid grid-cols-1 gap-x-4 gap-y-4 @md:grid-cols-2 @3xl:grid-cols-4">
          <div>
            <FieldLabel htmlFor="po-order-date">{t("purchaseOrders.orderDate")}</FieldLabel>
            <Input id="po-order-date" type="date" disabled={readOnly} value={h.orderDate} onChange={(e) => patch({ orderDate: e.target.value })} />
            <Hint>{t("poWorkspace.required")}</Hint>
          </div>
          <div>
            <FieldLabel htmlFor="po-expected">{t("purchaseOrders.expectedDelivery")}</FieldLabel>
            <Input id="po-expected" type="date" disabled={readOnly} value={h.expectedDeliveryDate} onChange={(e) => patch({ expectedDeliveryDate: e.target.value })} />
          </div>
          <div>
            <FieldLabel htmlFor="po-actual">{t("purchaseOrders.actualDelivery")}</FieldLabel>
            <Input id="po-actual" type="date" disabled={readOnly} value={h.actualDeliveryDate} onChange={(e) => patch({ actualDeliveryDate: e.target.value })} />
          </div>
          <div>
            <FieldLabel htmlFor="po-status">{t("common.status")}</FieldLabel>
            <Select id="po-status" containerClassName="w-full" disabled={readOnly} value={h.status} onChange={(e) => patch({ status: e.target.value as PoStatus })}>
              {PO_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {translateStatus(t, s)}
                </option>
              ))}
            </Select>
            <Hint>{t("poWorkspace.statusHint")}</Hint>
          </div>
          <div>
            <FieldLabel htmlFor="po-currency">{t("common.currencyLabel")}</FieldLabel>
            {/* Real select: a <datalist> only suggests entries matching the text already in the box, so "USD" showed USD
                alone. A legacy currency missing from the list (RMB/VND data) stays selectable. */}
            <Select id="po-currency" containerClassName="w-full" className="font-mono" disabled={readOnly} value={h.currency} onChange={(e) => patch({ currency: e.target.value })}>
              {!currencies.includes(h.currency) && <option value={h.currency}>{h.currency || "—"}</option>}
              {currencies.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </Select>
          </div>
          <div>
            <FieldLabel htmlFor="po-payment-terms">{t("purchaseOrders.paymentTerms")}</FieldLabel>
            <Select id="po-payment-terms" containerClassName="w-full" disabled={readOnly} value={h.paymentTerms} onChange={(e) => patch({ paymentTerms: e.target.value })}>
              <option value="">—</option>
              {paymentTermsOptions.map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
              {h.paymentTerms && !paymentTermsOptions.includes(h.paymentTerms) && (
                <option value={h.paymentTerms}>{h.paymentTerms}</option>
              )}
            </Select>
          </div>
          <div>
            <FieldLabel htmlFor="po-shipping-method">{t("purchaseOrders.shippingMethod")}</FieldLabel>
            <Select id="po-shipping-method" containerClassName="w-full" disabled={readOnly} value={h.shippingMethod} onChange={(e) => patch({ shippingMethod: e.target.value })}>
              <option value="">—</option>
              {shippingMethodOptions.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
              {h.shippingMethod && !shippingMethodOptions.includes(h.shippingMethod) && (
                <option value={h.shippingMethod}>{h.shippingMethod}</option>
              )}
            </Select>
          </div>
          <div>
            <FieldLabel htmlFor="po-priority">{t("common.priority")}</FieldLabel>
            <Select id="po-priority" containerClassName="w-full" disabled={readOnly} value={h.priority} onChange={(e) => patch({ priority: e.target.value })}>
              <option value="">—</option>
              {priorities.map((p) => (
                <option key={p} value={p}>
                  {translateStatus(t, p)}
                </option>
              ))}
              {h.priority && !priorities.includes(h.priority) && (
                <option value={h.priority}>{translateStatus(t, h.priority)}</option>
              )}
            </Select>
          </div>
        </div>
        {/* PI liên kết: lúc tạo là tuỳ chọn (chỉ để tham chiếu) nên gập lại; đơn đã lưu thì chỉ hiện PI đã nối. */}
        {isNew ? (
          <MoreBlock title={t("purchaseOrders.linkPi")} defaultOpen={false} filledLabel={linkedPi?.piNo ?? null}>
            <PoPiLinkPanel
              bare
              isNew
              linkedPiId={linkedPi?.id ?? null}
              linkedPiNo={linkedPi?.piNo ?? null}
              onPick={(pi: PiSummaryDto) => {
                setDirty(true);
                setLinkedPi({ id: pi.id, piNo: pi.piNo });
              }}
              onClear={() => {
                setDirty(true);
                setLinkedPi(null);
              }}
              disabled={readOnly || saving}
            />
          </MoreBlock>
        ) : (
          <PoPiLinkPanel bare isNew={false} linkedPiId={linkedPi?.id ?? null} linkedPiNo={linkedPi?.piNo ?? null} onPick={()=>undefined} onClear={()=>undefined}/>
        )}
      </FormGroup>

      {/* ── Hàng hoá — bảng độ rộng cố định; khối tổng tiền (thuế, phí vận chuyển) ngay dưới bảng. ── */}
      <FormGroup
        id="po-group-lines"
        flush
        title={t("poWorkspace.groups.lines")}
        hint={`${t("poWorkspace.lineHint")} ${t("poWorkspace.unitReviewHint")} ${t("poWorkspace.supplierVisibleLines")}`}
        actions={
          <Button
            type="button"
            variant="ghost"
            size="sm"
            disabled={sideUsed || readOnly}
            title={sideUsed ? t("poWorkspace.sideLocked") : t("poWorkspace.sideColumns")}
            aria-pressed={showSide}
            onClick={() => setSideOpen((open) => !open)}
          >
            {showSide ? t("poWorkspace.sideHide") : t("poWorkspace.sideShow")}
          </Button>
        }
      >
        {existing?.priceSnapshots?.length ? <div className="px-4"><DocumentPriceBasis snapshots={existing.priceSnapshots}/></div> : null}
        <PoLinesEditor
          lang={h.printLanguage}
          lines={lines}
          onChange={patchLines}
          currency={h.currency}
          supplierId={supplierId}
          supplierName={supplierName}
          readOnly={readOnly}
          showSide={showSide}
        />
        <div className="flex justify-end border-t border-line px-4 py-3">
          <dl className="grid w-full max-w-md grid-cols-[minmax(0,1fr)_11rem] items-center gap-x-4 gap-y-2 text-sm">
            <dt className="text-right text-ink-soft">{t("common.subtotal")}</dt>
            <dd className="text-right font-mono tabular text-ink">{formatMoney(totals.subtotal, h.currency)}</dd>
            <dt className="text-right"><label htmlFor="po-tax" className="text-ink-soft">{t("purchaseOrders.taxPercent")}</label></dt>
            <dd>
              <Input id="po-tax" inputMode="decimal" className={numberCls} trailing="%" disabled={readOnly} invalid={taxParsed.invalid}
                aria-invalid={taxParsed.invalid ? true : undefined} value={h.taxPercentage} onChange={(e) => patch({ taxPercentage: e.target.value })} placeholder="0" />
            </dd>
            <dt className="text-right text-ink-soft">{t("purchaseOrders.tax")}</dt>
            <dd className="text-right font-mono tabular text-ink">{formatMoney(totals.tax, h.currency)}</dd>
            <dt className="text-right"><label htmlFor="po-shipping" className="text-ink-soft">{t("poWorkspace.shippingFee")}</label></dt>
            <dd>
              <Input id="po-shipping" inputMode="decimal" className={numberCls} trailing={h.currency} disabled={readOnly} invalid={shippingParsed.invalid}
                aria-invalid={shippingParsed.invalid ? true : undefined} value={h.shipping} onChange={(e) => patch({ shipping: e.target.value })} placeholder="0.00" />
            </dd>
            <dt className="border-t border-line pt-2 text-right font-medium text-ink">{t("common.total")} · {h.currency}</dt>
            <dd className="border-t border-line pt-2 text-right font-mono tabular text-base font-semibold text-ink">{formatMoney(totals.total, h.currency)}</dd>
          </dl>
        </div>
      </FormGroup>

      <FormGroup id="po-group-notes" title={t("common.remarks")} hint={t("poWorkspace.supplierVisibleRemarks")}>
        <textarea
          id="po-remarks"
          aria-label={t("common.remarks")}
          rows={3}
          disabled={readOnly}
          value={h.remarks}
          onChange={(e) => patch({ remarks: e.target.value })}
          className="w-full rounded-sm border border-line-strong bg-surface px-2.5 py-2 text-sm text-ink outline-none focus:border-brand"
        />
      </FormGroup>

      {error && <p role="alert" className="rounded-sm bg-danger-tint px-3 py-2 text-xs text-danger">{error}</p>}
      {/* Một thanh Hủy / Lưu duy nhất, dính đáy (19/09/2026 — bỏ nút Lưu thứ hai ở đầu trang). */}
      <div className="sticky bottom-0 z-20 flex flex-wrap items-center justify-end gap-x-3 gap-y-2 rounded-md border border-line bg-surface px-4 py-3 shadow-[var(--shadow-card)]">
        <p className="mr-auto text-xs text-ink-faint">{dirty && !isNew ? t("poWorkspace.saveBeforePrint") : null}</p>
        <Button variant="secondary" disabled={busy} onClick={()=>navigate(listUrl)}>{t("common.cancel")}</Button>
        {canEdit && <SaveButton onClick={()=>void save()} saving={saving} savedAt={savedAt}/>}
      </div>
    </PageBody>
  );
}
